<div class="flex h-screen bg-slate-50 font-sans">

  <!-- LEFT PANEL: Selected Product Detail -->
  <div class="w-1/2 flex flex-col bg-white border-r border-slate-200">

    <!-- Header -->
    <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
      <div>
        <p class="text-xs font-semibold uppercase tracking-widest text-slate-400">Selected Product</p>
        <h2 class="text-base font-semibold text-slate-800 mt-0.5">Product Details</h2>
      </div>
      <span
        class="text-xs font-medium px-3 py-1 rounded-full"
        [ngClass]="getCategoryClass(selectedProduct.productCategory)"
      >
        {{ selectedProduct.productCategory }}
      </span>
    </div>

    <!-- Detail Body -->
    <div class="flex-1 overflow-y-auto p-6">

      <!-- Identity -->
      <div class="flex items-center gap-4 mb-6">
        <div
          class="w-14 h-14 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0"
          [ngClass]="getAvatarClass(selectedProduct.productCategory)"
        >
          {{ getInitials(selectedProduct.productName) }}
        </div>
        <div>
          <h3 class="text-xl font-semibold text-slate-800">{{ selectedProduct.productName }}</h3>
          <p class="text-sm text-slate-400 font-mono mt-0.5">{{ selectedProduct.productCode }}</p>
        </div>
      </div>

      <!-- Key Metrics Grid -->
      <div class="grid grid-cols-2 gap-3 mb-6">

        <div class="bg-slate-50 rounded-xl p-4">
          <p class="text-xs text-slate-400 font-medium mb-1">Loan Amount Range</p>
          <p class="text-sm font-semibold text-slate-700" *ngIf="selectedProduct.minLoanAmount && selectedProduct.maxLoanAmount">
            ₹{{ formatAmount(selectedProduct.minLoanAmount) }} –
            ₹{{ formatAmount(selectedProduct.maxLoanAmount) }}
          </p>
          <p class="text-sm text-slate-300 italic" *ngIf="!selectedProduct.minLoanAmount">Not configured</p>
        </div>

        <div class="bg-slate-50 rounded-xl p-4">
          <p class="text-xs text-slate-400 font-medium mb-1">Interest Rate</p>
          <p class="text-sm font-semibold text-slate-700" *ngIf="selectedProduct.minInterestRate && selectedProduct.maxInterestRate">
            {{ selectedProduct.minInterestRate }}% – {{ selectedProduct.maxInterestRate }}%
          </p>
          <p class="text-sm font-semibold text-emerald-600" *ngIf="selectedProduct.defaultInterestRate && !selectedProduct.minInterestRate">
            {{ selectedProduct.defaultInterestRate }}% (default)
          </p>
          <p class="text-sm text-slate-300 italic" *ngIf="!selectedProduct.minInterestRate && !selectedProduct.defaultInterestRate">Not configured</p>
        </div>

        <div class="bg-slate-50 rounded-xl p-4">
          <p class="text-xs text-slate-400 font-medium mb-1">Tenure Range</p>
          <p class="text-sm font-semibold text-slate-700" *ngIf="selectedProduct.minLoanTenure && selectedProduct.maxLoanTenure">
            {{ selectedProduct.minLoanTenure }} – {{ selectedProduct.maxLoanTenure }} months
          </p>
          <p class="text-sm text-slate-300 italic" *ngIf="!selectedProduct.minLoanTenure">Not configured</p>
        </div>

        <div class="bg-slate-50 rounded-xl p-4">
          <p class="text-xs text-slate-400 font-medium mb-1">Default Interest Rate</p>
          <p class="text-sm font-semibold text-emerald-600" *ngIf="selectedProduct.defaultInterestRate">
            {{ selectedProduct.defaultInterestRate }}%
          </p>
          <p class="text-sm text-slate-300 italic" *ngIf="!selectedProduct.defaultInterestRate">Not configured</p>
        </div>

      </div>

      <!-- Divider -->
      <div class="border-t border-slate-100 mb-5"></div>

      <!-- Secondary Info -->
      <div class="space-y-3">

        <div class="flex items-center justify-between text-sm">
          <span class="text-slate-400">Currency</span>
          <span class="font-medium text-slate-700" *ngIf="selectedProduct.currencyCode">{{ selectedProduct.currencyCode }}</span>
          <span class="text-slate-300 italic" *ngIf="!selectedProduct.currencyCode">—</span>
        </div>

        <div class="flex items-center justify-between text-sm">
          <span class="text-slate-400">Co-applicants</span>
          <span class="font-medium text-slate-700" *ngIf="selectedProduct.minCoApplicant !== null">
            {{ selectedProduct.minCoApplicant }} – {{ selectedProduct.maxCoApplicant }}
          </span>
          <span class="text-slate-300 italic" *ngIf="selectedProduct.minCoApplicant === null">—</span>
        </div>

        <div class="flex items-center justify-between text-sm">
          <span class="text-slate-400">Guarantors</span>
          <span class="font-medium text-slate-700" *ngIf="selectedProduct.minGuarantors !== null">
            {{ selectedProduct.minGuarantors }} – {{ selectedProduct.maxGuarantors }}
          </span>
          <span class="text-slate-300 italic" *ngIf="selectedProduct.minGuarantors === null">—</span>
        </div>

        <div class="flex items-center justify-between text-sm">
          <span class="text-slate-400">Effective From</span>
          <span class="font-medium text-slate-700" *ngIf="selectedProduct.effectiveFrom">{{ formatDate(selectedProduct.effectiveFrom) }}</span>
          <span class="text-slate-300 italic" *ngIf="!selectedProduct.effectiveFrom">—</span>
        </div>

        <div class="flex items-center justify-between text-sm">
          <span class="text-slate-400">Expire Date</span>
          <span class="font-medium text-slate-700" *ngIf="selectedProduct.expireDate">{{ formatDate(selectedProduct.expireDate) }}</span>
          <span class="text-slate-300 italic" *ngIf="!selectedProduct.expireDate">—</span>
        </div>

      </div>

      <!-- Divider -->
      <div class="border-t border-slate-100 my-5"></div>

      <!-- Proceed Button -->
      <button
        class="w-full py-3 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 transition-all duration-150"
        (click)="onProceed()"
      >
        Proceed with {{ selectedProduct.productName }} →
      </button>

    </div>
  </div>

  <!-- RIGHT PANEL: Product List -->
  <div class="w-1/2 flex flex-col bg-slate-50">

    <!-- Header -->
    <div class="px-6 py-4 border-b border-slate-200 bg-white">
      <div class="flex items-center justify-between mb-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-widest text-slate-400">All Products</p>
          <h2 class="text-base font-semibold text-slate-800 mt-0.5">
            {{ filteredProducts.length }} of {{ products.length }} products
          </h2>
        </div>
      </div>

      <!-- Search -->
      <div class="relative mb-3">
        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"/>
        </svg>
        <input
          type="text"
          placeholder="Search by name or code…"
          [(ngModel)]="searchQuery"
          (ngModelChange)="applyFilters()"
          class="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent transition"
        />
      </div>

      <!-- Category Filters -->
      <div class="flex gap-2 flex-wrap">
        <button
          class="text-xs px-3 py-1 rounded-full font-medium border transition-all duration-150"
          [ngClass]="activeCategory === 'ALL' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300'"
          (click)="setCategory('ALL')"
        >
          All
        </button>
        <button
          *ngFor="let cat of categories"
          class="text-xs px-3 py-1 rounded-full font-medium border transition-all duration-150"
          [ngClass]="activeCategory === cat ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300'"
          (click)="setCategory(cat)"
        >
          {{ cat }}
        </button>
      </div>
    </div>

    <!-- Product List -->
    <div class="flex-1 overflow-y-auto p-3 space-y-2">

      <div
        *ngFor="let product of filteredProducts"
        class="flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-150"
        [ngClass]="product.id === selectedProduct.id
          ? 'bg-indigo-50 border-indigo-300 shadow-sm'
          : 'bg-white border-slate-200 hover:border-indigo-200 hover:bg-slate-50'"
        (click)="selectProduct(product)"
      >
        <!-- Avatar -->
        <div
          class="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0"
          [ngClass]="getAvatarClass(product.productCategory)"
        >
          {{ getInitials(product.productName) }}
        </div>

        <!-- Info -->
        <div class="flex-1 min-w-0">
          <p
            class="text-sm font-semibold truncate"
            [ngClass]="product.id === selectedProduct.id ? 'text-indigo-700' : 'text-slate-700'"
          >
            {{ product.productName }}
          </p>
          <p class="text-xs font-mono text-slate-400 mt-0.5">{{ product.productCode }}</p>
        </div>

        <!-- Loan Amount Badge -->
        <div class="text-right flex-shrink-0" *ngIf="product.maxLoanAmount">
          <p class="text-xs text-slate-400">up to</p>
          <p class="text-xs font-semibold text-slate-600">₹{{ formatAmount(product.maxLoanAmount) }}</p>
        </div>

        <!-- Active Check -->
        <div
          class="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
          [ngClass]="product.id === selectedProduct.id ? 'bg-indigo-600' : 'border border-slate-200'"
        >
          <svg *ngIf="product.id === selectedProduct.id" class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
          </svg>
        </div>
      </div>

      <!-- Empty State -->
      <div *ngIf="filteredProducts.length === 0" class="text-center py-16 text-slate-400">
        <svg class="w-10 h-10 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 0 1 5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>
        </svg>
        <p class="text-sm font-medium">No products found</p>
        <p class="text-xs mt-1">Try adjusting your search or filters</p>
      </div>

    </div>
  </div>

</div>